import { useCallback, useEffect, useState } from 'react';
import { useLazyGetLastNotificationQuery } from '#entities/notification';
import type { OpenApiSchemaNotification } from '#shared/api/schema';
import {
    AUTOMATIC_PRESENTATIONS,
    type AutomaticPresentationEligibility,
    useAutomaticPresentation,
} from '#shared/lib/automaticPresentation';
import { useDeferred } from '#shared/lib/hooks';
import { AI_ASSISTANT_NOTIFICATION_OPEN_DELAY_MS } from './notificationConstants';
import { setAIAssistantConsent } from './setAIAssistantConsent';

/**
 * Добавляет автоматическую нотификацию Alty в общую очередь до ответа сервера и загрузки интерфейса.
 * Пока результат неизвестен, Dashboard и News ждут; отсутствие нотификации или ошибка продолжают очередь.
 *
 * @kind helper
 * @param openFromNotification Функция открытия Embedded Alty; `null` означает, что интерфейс ещё загружается.
 * @returns Нотификацию для окна Alty и обработчик, который отменяет её автоматическое открытие.
 */
export function useAIAssistantNotificationPresentation(openFromNotification: VoidFunction | null): {
    notification: OpenApiSchemaNotification | null;
    /** Отменяет автоматическое открытие текущей нотификации, когда пользователь уже открыл Alty сам. */
    cancelAutomaticOpening: VoidFunction;
} {
    const [triggerGet, { data: notification = null }] = useLazyGetLastNotificationQuery();
    const [notificationEligibility, setNotificationEligibility] = useState<AutomaticPresentationEligibility>('pending');
    const [isAutomaticOpeningCancelled, setAutomaticOpeningCancelled] = useState(false);
    // После ручного открытия автоматический показ больше не нужен: даже поздний ответ сервера не должен открыть Alty повторно.
    const eligibility = isAutomaticOpeningCancelled ? 'ineligible' : notificationEligibility;
    const deferred = useDeferred();
    const { isActive, startLeaving, completeLeaving } = useAutomaticPresentation({
        ...AUTOMATIC_PRESENTATIONS.HIGH.altyNotification,
        // Пока сервер не ответил, нотификация удерживает свою позицию; пустой ответ или ошибка освобождают очередь.
        eligibility,
        // Открыть окно можно только после загрузки Embedded и получения функции открытия.
        isReady: openFromNotification !== null,
    });

    useEffect(() => {
        if (!deferred) {
            return;
        }

        let isCurrentRequest = true;

        void triggerGet()
            .unwrap()
            .then((response: OpenApiSchemaNotification | null) => {
                if (!isCurrentRequest) {
                    return;
                }

                if (response === null) {
                    setNotificationEligibility('ineligible');

                    return;
                }

                setAIAssistantConsent(true);
                setNotificationEligibility('eligible');
            })
            .catch(() => {
                if (isCurrentRequest) {
                    setNotificationEligibility('ineligible');
                }
            });

        return () => {
            isCurrentRequest = false;
        };
    }, [deferred, triggerGet]);

    const cancelAutomaticOpening = useCallback(() => {
        setAutomaticOpeningCancelled(true);
    }, []);

    useEffect(() => {
        if (!isActive || !openFromNotification) {
            return;
        }

        const timeoutId = setTimeout(() => {
            if (!startLeaving('completed')) {
                return;
            }

            // Завершаем нотификацию до открытия окна, чтобы после закрытия Alty она не попала в очередь повторно.
            completeLeaving();
            openFromNotification();
        }, AI_ASSISTANT_NOTIFICATION_OPEN_DELAY_MS);

        return () => {
            clearTimeout(timeoutId);
        };
    }, [completeLeaving, isActive, openFromNotification, startLeaving]);

    return { notification, cancelAutomaticOpening };
}
